import React from "react";
import { Select } from "antd";
import { includes, map } from "lodash";
import { AICC_SANDBOX_LESSON } from "constants/aiccPromptSandbox";
import { AiccLessonReviewScript } from "types/model/aiccLearningAssets";
import { SandboxLessonState } from "./lessonRequest";
import { lessonScripts, lessonValues, levelValues, scriptOptions, selectedScript } from "./lessonScripts";
import {
  LESSON_DESCRIPTION,
  LESSON_LESSON_LABEL,
  LESSON_LEVEL_LABEL,
  LESSON_NO_SCRIPTS,
  LESSON_PREVIEW_CUE,
  LESSON_PREVIEW_LEARNER,
  LESSON_SCRIPT_INDEX_LABEL,
} from "../configs";
import styles from "../Sandbox.module.scss";

interface Props {
  lesson: SandboxLessonState;
  lessons: AiccLessonReviewScript[];
  isScriptsLoading: boolean;
  isFixed: boolean;
  onChange: (lesson: SandboxLessonState) => void;
}

function LessonForm({ lesson, lessons, isScriptsLoading, isFixed, onChange }: Props) {
  const set = (changes: Partial<SandboxLessonState>) => onChange({ ...lesson, ...changes });

  const levels = levelValues(lessons);
  const numbers = lessonValues(lessons, lesson.level);
  const scripts = lessonScripts(lessons, lesson.level, lesson.lesson);
  const script = selectedScript(lessons, lesson.level, lesson.lesson, lesson.scriptIndex);

  const pickLevel = (level: number) => {
    const [firstLesson = AICC_SANDBOX_LESSON.defaultLesson] = lessonValues(lessons, level);
    const [firstScript] = lessonScripts(lessons, level, firstLesson);

    set({
      level,
      lesson: firstLesson,
      scriptIndex: firstScript?.scriptIndex ?? AICC_SANDBOX_LESSON.defaultScriptIndex,
    });
  };

  const pickLesson = (value: number) => {
    const [firstScript] = lessonScripts(lessons, lesson.level, value);

    set({ lesson: value, scriptIndex: firstScript?.scriptIndex ?? AICC_SANDBOX_LESSON.defaultScriptIndex });
  };

  const select = (label: string, value: number, values: number[], onPick: (value: number) => void) => (
    <label className={styles.lessonNumber}>
      <span className={styles.fieldLabel}>{label}</span>
      <Select
        size="small"
        className={styles.lessonSelect}
        value={includes(values, value) ? value : undefined}
        options={map(values, (option) => ({ value: option, label: String(option) }))}
        loading={isScriptsLoading}
        disabled={isFixed}
        onChange={onPick}
      />
    </label>
  );

  return (
    <div className={styles.inputs}>
      <div className={styles.inputsDescription}>{LESSON_DESCRIPTION}</div>

      <div className={styles.lessonNumbers}>
        {select(LESSON_LEVEL_LABEL, lesson.level, levels, pickLevel)}
        {select(LESSON_LESSON_LABEL, lesson.lesson, numbers, pickLesson)}
        <label className={styles.lessonNumber}>
          <span className={styles.fieldLabel}>{LESSON_SCRIPT_INDEX_LABEL}</span>
          <Select
            size="small"
            className={styles.lessonSelectWide}
            value={script?.scriptIndex}
            options={scriptOptions(scripts)}
            loading={isScriptsLoading}
            disabled={isFixed}
            onChange={(value) => set({ scriptIndex: value })}
          />
        </label>
      </div>

      {!!script && (
        <div className={styles.lessonPreview}>
          {!!script.sceneSetup && <div className={styles.lessonScene}>{script.sceneSetup}</div>}
          {map(script.lines, ({ speaker, cue, text }, index) => (
            <div key={index} className={styles.lessonLine}>
              <span
                className={speaker === AICC_SANDBOX_LESSON.learnerSpeaker ? styles.lessonLearner : styles.lessonSpeaker}
              >
                {speaker === AICC_SANDBOX_LESSON.learnerSpeaker ? LESSON_PREVIEW_LEARNER : speaker}
              </span>
              <span className={styles.lessonText}>{text}</span>
              {!!cue && <span className={styles.lessonCue}>{`${LESSON_PREVIEW_CUE} ${cue}`}</span>}
            </div>
          ))}
        </div>
      )}

      {!script && !isScriptsLoading && <div className={styles.fieldNote}>{LESSON_NO_SCRIPTS}</div>}
    </div>
  );
}

export default LessonForm;
